import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Chart from 'react-apexcharts';
import { Card, CardBody, CardExpandToggler } from './../../components/card/card.jsx';
import toastService from '../../services/toastService.js';

// Import custom hooks
import { usePentestScan } from './hooks/usePentestScan.js';
import { useScanData } from './hooks/useScanData.js';
import { useFileOperations } from './hooks/useFileOperations.js';
import { usePdfGeneration } from './hooks/usePdfGeneration.js';
import { useScanners } from './hooks/useScanners.js';
import useAuth from '../../hooks/useAuth.js';
import useConfirmDialog from '../../hooks/useConfirmDialog.js';

// Import utility functions
import { getChartOptions } from './utils/chartUtils.js';
import getPentestServer from './utils/getPentestServer.js';

// Import components
import ScanForm from './components/ScanForm.jsx';
import ProgressLog from './components/ProgressLog.jsx';
import ScanResults from './components/ScanResults.jsx';
import FileModal from './components/FileModal.jsx';
import ConfirmDialog from '../../components/ui/ConfirmDialog.jsx';

// Import DataTables CSS
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';
import 'datatables.net-responsive-bs5/css/responsive.bootstrap5.min.css';
import 'datatables.net-fixedcolumns-bs5/css/fixedColumns.bootstrap5.min.css';
import 'datatables.net-buttons-bs5/css/buttons.bootstrap5.min.css';

function SmallPentest() {
  // Configuration
  const pentestServer = getPentestServer();

  // Authentication and user data
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();

    // Get user role and info from authenticated user - we need block unautorized users!
  const userRole = user?.role || 'user';
  const userName = user?.username || 'Guest';
  const userEmail = user?.email || '';
  
  // Confirm dialog hook
  const { dialogState, showConfirm } = useConfirmDialog();
  
  // State for chart options
  const [chartOptions, setChartOptions] = useState(null);
  
  // Toast notifications are now handled by toastService

  // Custom hooks - order matters for dependencies
  const {
    tableData,
    statsData,
    selectedScans,
    isDeleting,
    refreshTableData,
    clearTableData,
    handleScanSelection,
    handleSelectAll,
    handleDeleteSelected,
    handleDeleteScan
  } = useScanData(pentestServer, showConfirm);

  const {
    scanId,
    isPentestLoading,
    pentestResult,
    progressLogs,
    progressPercentage,
    handlePentestSubmit,
    siteUrl,
    setSiteUrl
  } = usePentestScan(pentestServer, refreshTableData);

  // Scanner management hook
  const {
    scanners,
    selectedTools,
    isLoadingScanners,
    scannersError,
    scannersByCategory,
    availablePresets,
    hasToolPermission,
    handleToolSelection,
    handleSelectAll: handleSelectAllTools,
    handleClearAll: handleClearAllTools,
    applyPreset
  } = useScanners(pentestServer, userRole);

  // Create a wrapper function to pass user info and selected tools to the submit handler
  const handleFormSubmit = useCallback((e, selectedToolsFromForm) => {
    console.log('SmallPentest: Current user object:', user);
    console.log('SmallPentest: Selected tools:', selectedToolsFromForm);
    const userInfo = {
      username: user?.username || 'Guest',
      id: user?.id,
      role: user?.role || 'user',
      email: user?.email || ''
    };
    console.log('SmallPentest: Created userInfo for scan:', userInfo);
    handlePentestSubmit(e, userInfo, selectedToolsFromForm || selectedTools);
  }, [handlePentestSubmit, user, selectedTools]);

  const {
    resultFiles,
    showFileModal,
    closeFileModal,
    selectedFile,
    fileContent,
    fileContentType,
    fileLoading: isLoadingFile,
    fetchResultFiles,
    viewFileContent,
  } = useFileOperations(pentestServer);

  const {
    generatingPdf,
    pdfStatuses,
    handleGeneratePdf
  } = usePdfGeneration(
    pentestServer,
    refreshTableData,
    fetchResultFiles,
    scanId,
    isPentestLoading,
    showConfirm
  );


  // Helper function to check user permissions
  const hasPermission = (requiredRole) => {
    const roleHierarchy = { 'user': 1, 'superuser': 2, 'admin': 3 };
    const userRoleLevel = roleHierarchy[userRole] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;
    return userRoleLevel >= requiredRoleLevel;
  };
  
  const isAdmin = hasPermission('admin');
  const isSuperUser = hasPermission('superuser');

  // Effect to set chart options based on theme colors
  useEffect(() => {
    setChartOptions(getChartOptions());
  }, []);

  // First table render
  useEffect(() => {
    refreshTableData();
  }, []);

  // Enhanced PDF generation handler with alerts
  const handleGeneratePdfWithAlerts = useCallback(async (eventOrScanId, maybeScanId) => {
    let scanId = eventOrScanId && typeof eventOrScanId === 'object' && 'preventDefault' in eventOrScanId
      ? maybeScanId
      : eventOrScanId;
    toastService.info('Generating PDF report...');
    try {
      await handleGeneratePdf(eventOrScanId, maybeScanId);
      toastService.success('PDF report generated successfully!');
    } catch (error) {
      toastService.error('Failed to generate PDF report. Please try again.');
    }
  }, [handleGeneratePdf]);

  // Function to clear table data
  const handleClearTable = useCallback(async () => {
    const confirmed = await showConfirm({
      title: 'Clear Table Data',
      message: 'Are you sure you want to clear all scan results from the table? This will only clear the display, not delete the actual scan data.',
      confirmText: 'Clear Table',
      confirmVariant: 'warning'
    });
    
    if (confirmed) {
      clearTableData();
      toastService.info('Table cleared successfully!');
    }
  }, [clearTableData, showConfirm]);

  // Memoized refresh files handler
  const handleRefreshFiles = useCallback(() => {
    fetchResultFiles(scanId);
  }, [fetchResultFiles, scanId]);

  // Handlers object to consolidate all event handlers (memoized to prevent unnecessary re-renders)
  const handlers = useMemo(() => ({
    onScanSelection: handleScanSelection,
    onSelectAll: handleSelectAll,
    onDeleteSelected: handleDeleteSelected,
    // alias to match ScanResults usage
    handleDeleteSelected: handleDeleteSelected,
    onDeleteScan: handleDeleteScan,
    onGeneratePdf: handleGeneratePdfWithAlerts,
    onViewFile: viewFileContent,
    onRefreshFiles: handleRefreshFiles,
    refreshTableData: refreshTableData,
    onClearTable: handleClearTable,
  }), [handleScanSelection, handleSelectAll, handleDeleteSelected, handleDeleteScan, handleGeneratePdfWithAlerts, viewFileContent, handleRefreshFiles, refreshTableData, handleClearTable]);

  const stat = statsData;
  const isPentestLogVisible = isPentestLoading || (scanId !== null && progressLogs.length > 0);
  const currentScanId = scanId;
  
  // Toast notifications are handled by toastService - no local function needed
  const handleCloseFileModal = async () => {
    closeFileModal();
    await refreshTableData();
    document.getElementById('scan-results-table')?.scrollIntoView({ behavior: 'smooth' });
  };


  // Show loading state while authentication is being verified
  if (authLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Show authentication required message if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="alert alert-warning" role="alert">
        <h4 className="alert-heading">Authentication Required</h4>
        <p>You must be logged in to access the Small Pentest functionality.</p>
        <hr />
        <p className="mb-0">Please log in to continue.</p>
      </div>
    );
  }

  return (
    <div>
      <ul className="breadcrumb">
        <li className="breadcrumb-item"><a href="#/">Admin</a></li>
        <li className="breadcrumb-item active">Small Pentest (landing)</li>
      </ul>
    
      <h1 className="page-header">
        Small Pentest <small>statistic and data from small pentesting (landing page)</small>
        {user && (
          <div className="float-end">
            <small className="text-muted">
              Welcome, <strong>{userName}</strong> 
              <span className={`badge ms-2 ${
                userRole === 'admin' ? 'bg-danger' : 
                userRole === 'superuser' ? 'bg-warning' : 'bg-secondary'
              }`}>
                {userRole.toUpperCase()}
              </span>
              {userEmail && <span className="ms-2">• {userEmail}</span>}
            </small>
          </div>
        )}
      </h1>

      {/* Toast notifications are now handled by ToastContainer in App.jsx */}
      
      {/* Scanner Loading Error */}
      {scannersError && (
        <div className="alert alert-danger" role="alert">
          <div className="d-flex align-items-center">
            <i className="fa fa-exclamation-triangle me-2"></i>
            <div>
              <strong>Scanner Loading Error:</strong>
              <p className="mb-0 mt-1">{scannersError}</p>
              <small className="text-muted">Some scanning tools may not be available. Please refresh the page or contact support.</small>
            </div>
          </div>
        </div>
      )}
      
      {/* User Access Information */}
      {!isAdmin && (
        <div className="alert alert-info" role="alert">
          <div className="d-flex align-items-center">
            <i className="fa fa-info-circle me-2"></i>
            <div>
              <strong>Access Level: {userRole.toUpperCase()}</strong>
              {userRole === 'user' && (
                <p className="mb-0 mt-1">
                  You have basic access to run pentests and view your own results. 
                  Contact an administrator for elevated permissions.
                </p>
              )}
              {userRole === 'superuser' && (
                <p className="mb-0 mt-1">
                  You have elevated access to run pentests and view most results. 
                  Some administrative functions require admin privileges.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-xl-3 col-lg-6 pb-3" key="1">
          <Card className="mb-3">
            <CardBody>
              <div className="d-flex fw-bold small mb-3">
                <span className="flex-grow-1">Pentests runs</span>
              </div>
              <div className="row align-items-center mb-2">
                <div className="col-7">
                  <h3 className="mb-0">{stat.total}</h3>
                </div>
                <div className="col-5">
                  <div className="mt-n2">
                    {chartOptions && chartOptions[stat.chartType] ? (
                      <Chart type={stat.chartType} height={stat.chartHeight} options={chartOptions[stat.chartType]} series={stat.chartData} />
                    ) : (
                      <div>Loading chart...</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="small text-inverse text-opacity-50 text-truncate">
                {stat.info.length > 0 && stat.info.map((info, index) => (
                  <div key={index}><i className={info.icon}></i> {info.text}</div>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="col-xl-9 col-lg-6 pb-3" key="2">
          
              <ScanForm
                siteUrl={siteUrl}
                setSiteUrl={setSiteUrl}
                onSubmit={handleFormSubmit}
                isLoading={isPentestLoading}
                userRole={userRole}
                userName={userName}
                // Tool selection props
                scanners={scanners}
                selectedTools={selectedTools}
                scannersByCategory={scannersByCategory}
                availablePresets={availablePresets}
                hasToolPermission={hasToolPermission}
                onToolSelection={handleToolSelection}
                onSelectAll={handleSelectAllTools}
                onClearAll={handleClearAllTools}
                onApplyPreset={applyPreset}
                isLoadingScanners={isLoadingScanners}
              />
              <ProgressLog 
                isVisible={isPentestLogVisible}
                isLoading={isPentestLoading}
                progressPercentage={progressPercentage}
                progressLogs={progressLogs}
                pentestResult={pentestResult}
                resultFiles={resultFiles}
                currentScanId={currentScanId}
                onViewFile={handlers.onViewFile}
                onRefreshFiles={handlers.onRefreshFiles}
              />

        </div>
      </div>

      <ScanResults 
        handlers={handlers}
        tableData={tableData}
        selectedScans={selectedScans}
        userRole={userRole}
        isDeleting={isDeleting}
        statsData={statsData}
        generatingPdf={generatingPdf}
        pdfStatuses={pdfStatuses}
        currentUser={user}
        hasPermission={hasPermission}
      />

      <FileModal 
        isVisible={showFileModal}
        onClose={handleCloseFileModal}
        selectedFile={selectedFile}
        fileContent={fileContent}
        fileContentType={fileContentType}
        isLoading={isLoadingFile}
      />

      <ConfirmDialog 
        isVisible={dialogState.isVisible}
        title={dialogState.title}
        message={dialogState.message}
        confirmText={dialogState.confirmText}
        cancelText={dialogState.cancelText}
        confirmVariant={dialogState.confirmVariant}
        size={dialogState.size}
        onConfirm={dialogState.onConfirm}
        onCancel={dialogState.onCancel}
      />
    </div>
  );
}

export default SmallPentest;